import React from 'react';
import { Formik } from 'formik';
import moment from 'moment';
import * as yup from 'yup';
import classNames from 'classnames';
import {
  InputText,
  RadioButton,
  Layout,
  Title,
  Text,
  Button,
} from '../components';

const cancellations = [
  {
    label:
      "I'm moving to another rental, and I don't wish to transfer my policy",
    value: 'moving',
  },
  {
    label: 'I purchased a new home',
    value: 'new home',
  },
  {
    label: "Rates are too expensive, and I don't wish to adjust my coverages",
    value: 'rates',
  },
  {
    label: 'I purchased insurance from another company',
    value: 'purchased',
  },
  {
    label: "I'm no longer required to carry insurance",
    value: 'required',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

const validationSchema = yup.object({
  reason: yup.string().required('Reason is required'),
  other: yup.string().when('reason', {
    is: 'other',
    then: yup.string().required('Please provide a reason'),
  }),
  cancellationDate: yup
    .date()
    .min(
      moment().format('YYYY-MM-DD'),
      'Cancellation date cannot be in the past'
    )
    .required('Cancellation date is required'),
  fullName: yup.string().required('Full name is required'),
  streetAddress: yup.string().required('Street address is required'),
  line2: yup.string(),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  zipCode: yup.string().required('Zip code is required'),
});

const initialFormValues = {
  reason: '',
  other: '',
  fullName: '',
  streetAddress: '',
  line2: '',
  city: '',
  state: '',
  zipCode: '',
};

export function CancellationForm({ handleSubmit, isMobile }) {
  const buttonClass = classNames(isMobile ? 'w-100' : 'w-50', 'mt-10');
  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialFormValues}
      onSubmit={handleSubmit}
    >
      {({
        handleSubmit,
        handleChange,
        setFieldValue,
        handleBlur,
        values,
        errors,
        touched,
      }) => (
        <form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Layout display="flex-column" gap="medium" padding="none">
            <Layout display="block" padding="none" gap="large">
              <fieldset
                onChange={e => {
                  setFieldValue('reason', e.target.value);
                }}
                value={values.reason}
                name="reason"
                id="reason"
              >
                {cancellations.map((cancellation, index) => (
                  <RadioButton
                    key={index}
                    label={cancellation.label}
                    value={cancellation.value}
                    name="reason"
                    id={`reason${index}`}
                  />
                ))}
              </fieldset>
            </Layout>
            {values.reason === 'other' && (
              <InputText
                label="Please specify"
                name="other"
                id="other"
                placeholder="Please specify"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.other}
                touched={touched.other}
                helpText={errors.other}
                error={errors.other}
              />
            )}
            <InputText
              type="date"
              label="Cancellation date"
              min={moment().format('YYYY-MM-DD')}
              onChange={handleChange}
              id="cancellationDate"
              name="cancellationDate"
              helpText={errors.cancellationDate}
              error={errors.cancellationDate}
              touched={touched.cancellationDate}
            />
            <Text variant="caption" color="foreground-secondary">
              As of the effective date selected, your policy will be cancelled,
              and you will no longer have coverage.
            </Text>
            <Title secondary variant="title-3">
              Billing Address
            </Title>
            <Layout display="flex-column" padding="none" gap="medium">
              <InputText
                label="Full name"
                name="fullName"
                id="fullName"
                value={values.fullName}
                error={errors.fullName}
                onChange={handleChange}
                helpText={errors.fullName}
                onBlur={handleBlur}
                touched={touched.fullName}
              />
              <InputText
                label="Street address"
                name="streetAddress"
                value={values.streetAddress}
                error={errors.streetAddress}
                onChange={handleChange}
                helpText={errors.streetAddress}
                onBlur={handleBlur}
                touched={touched.streetAddress}
              />
              <InputText
                label="Unit or apartment number"
                name="line2"
                optional
                value={values.line2}
                error={errors.line2}
                onBlur={handleBlur}
                onChange={handleChange}
                helpText={errors.line2}
                touched={touched.line2}
              />
              <InputText
                label="City"
                value={values.city}
                error={errors.city}
                name="city"
                onBlur={handleBlur}
                onChange={handleChange}
                helpText={errors.city}
                touched={touched.city}
              />
              <InputText
                label="State"
                value={values.state}
                error={errors.state}
                onChange={handleChange}
                helpText={errors.state}
                onBlur={handleBlur}
                touched={touched.state}
                name="state"
              />
              <InputText
                label="Zip"
                value={values.zipCode}
                error={errors.zipCode}
                name="zipCode"
                onChange={handleChange}
                helpText={errors.zipCode}
                touched={touched.zipCode}
                onBlur={handleBlur}
              />
            </Layout>
            <Button submit className={buttonClass} variant="primary">
              Cancel my policy
            </Button>
          </Layout>
        </form>
      )}
    </Formik>
  );
}
